import DEFAULT_UI_CONFIGURATION_json from "./DEFAULT_UI_CONFIGURATION.json";
/**
 * Describes a locale description item to use with locale switcher
 */
export interface LocalizationLocale {
  /**
   * The locale string, eg. 'en', 'fi'. If region is included, separate with '-' and use lowercase
   */
  value: string;
  /**
   * Name of the locale/language in that language, eg. 'English', 'Suomi'
   */
  label: string;
}

/**
 * Describes the configuration for the locale switcher
 */
export interface LocalizationConfiguration {
  /**
   * Toggles the availability of the tool.
   * Defaults are used if missing.
   */
  "show-switcher"?: boolean;
  /**
   * Defines the locales to include.
   * Please note that there is no guarantee that resources for those locales exist. It is the responsibility
   * of the developer to add matching resource files separately, this only controls what is potentially available.
   * If missing, default set is used. Empty array is considered as undefined and ignored.
   */
  locales?: LocalizationLocale[];
  /**
   * default locale to use
   */
  default?: string;
  /**
   * location of translated assets
   */
  translationsUrl?: string;

  /**
   * Enables automatic detection of preferred locales with navigator.languages
   */
  autoDetect?: boolean;

}

/**
 * Describes a list of menu links.
 */
export interface MenuLinkConfiguration {
  /**
   * The link name does not allow spaces, but underscores will be rendered as spaces in the menu.
   */
  [link: string]: string
}

/**
 * Describes the session panel menu.
 */
export interface MenuConfiguration {
  /**
   * Links to show in menu, Logout is always shown.
   *  Format: {
   *  "link_name": "url"
   *  }
   *  The link name must not contain spaces and special character support is limited. (localisation Message formatting documentation does not provide exact documentation)
   *  The link name will be displayed as default label, but can be localized with localisation setup. Underscores will be displayed as spaces.
   *  The link name is also used to create an id for the link node to enable adding e.g. icons with css. Link name is formed by prepending "menu-link-" to each link_name.
   *  The url supports 10duke locale parameter injection with ${locale}
   */
  links: MenuLinkConfiguration
}

export interface MyLicensesConf {
  /**
   * Date formatting, auto uses short date according to active locale.
   * Custom values support "dayjs" formatting patterns
   */
  "date-format"?: "auto"|string,

}

/**
 * Describes the topics that can be configured
 */
export interface FunctionalityConfiguration {

  /**
   * MS how long before expiration a license is marked as "expiring". Defaults to 7 days.
   */
  expiringInMs?: number;

  /**
   * Session panel menu configuration
   */
  menu?: MenuConfiguration;
  /**
   * my-licenses page conf
   */
  "my-licenses": MyLicensesConf;
  /**
   * Configuration for the Localization
   * If missing, defaults are used.
   */
  localization?: LocalizationConfiguration;

  /**
   * Optional JSON file that can be used to provide additional data to loaded licenses by mapping the licensed item names
   */
  additionalLicenseDataURI?: string;
}

export interface SecurityConfiguration {
  /**
   * Maximum allowed leeway / clock skew when authenticating and checking iat (issued at)
   * timestamp of OpenID Connect ID token. The value is in seconds.
   */
  idTokenIatLeeway?: number;
}

/**
 * Configuration for the SsoUIDefault Webapp
 */
export interface MyLicensesConfiguration {
  /**
   * The version of the app
   */
  version?: string;

  /**
   * Wraps the actual configurable topics
   * If missing, defaults are used.
   */
  functionality?: FunctionalityConfiguration;

  /**
   * Security related configurations.
   * If missing, defaults are used.
   */
  security?: SecurityConfiguration;
}

/**
 * Intentionally defined in a separate JSON for easy copy-paste base for configs and to enable use in e.g. build tools
 */
export const DEFAULT_UI_CONFIGURATION: MyLicensesConfiguration =
  DEFAULT_UI_CONFIGURATION_json as MyLicensesConfiguration;
